import dayjs from 'dayjs';
import React from 'react';

import { getObservationConfig } from '@constants/fhir';

import { useObservations } from '@hooks/api/vital-sign';
import { useLazyGetMetricDateRange } from '@hooks/useGetMetricDateRange';
import { useSession } from '@hooks/useSession';

import type { DataPointMapped } from '@models/data-point';
import { DAYJS_MAP, Interval, MetricVisual } from '@models/graph';
import { TrackerType } from '@models/vital-sign';

import { getReferenceRangeMap } from '@utils/reference_range';

import { ReferenceRangeRiskLevel } from 'generated/types';
import type { ReferenceRangeModel } from 'generated/types';

interface State {
  interval: Interval;
  range: number;
  minDate: Date;
  maxDate: Date;
  isInitialized: boolean;
  sources?: string[] | null;
  clinicalStatus?: boolean | null;
  view: MetricVisual;
  /**
   * Should the alerts of an observation be fetched and displayed?
   * @default true (vital-signs, body, daily, sleep) - false for health scores
   * If false, getAlertStats query will be skipped and alert table columns are hidden
   */
  showAlerts: boolean;
}

interface IContext {
  state: State;
  dispatch: React.Dispatch<Action<Payload>>;
  loading: boolean;
  data: DataPointMapped[];
  category: TrackerType;
  code: string;
  referenceRangeMap: Map<
    ReferenceRangeRiskLevel,
    ReferenceRangeModel['risks'][number]
  >;
  referenceRange?: ReferenceRangeModel;
  showAlerts?: boolean;
}

enum Actions {
  SetFilters = 'setFilters',
  Initialize = 'initialize',
  SetData = 'setData',
  SetInterval = 'setInterval',
  SetView = 'setView',
}

type Payload = {
  [Actions.SetFilters]: {
    minDate?: Date;
    maxDate?: Date;
    interval?: Interval;
    sources?: string[];
    clinicalStatus?: boolean;
    range?: number;
  };
  [Actions.Initialize]: {
    minDate: Date;
    maxDate: Date;
    interval: Interval;
    range: number;
    showAlerts?: boolean;
  };
  [Actions.SetInterval]: {
    interval: Interval;
  };
  [Actions.SetView]: {
    view: MetricVisual;
  };
};

const initialState: State = {
  interval: Interval.Week,
  range: 1,
  maxDate: new Date(),
  minDate: dayjs().subtract(1, 'week').toDate(),
  isInitialized: false,
  sources: null,
  clinicalStatus: null,
  view: MetricVisual.Graph,
  showAlerts: true,
};

function reducer(state: State, action: Action<Payload>): State {
  switch (action.type) {
    case Actions.SetFilters:
      return {
        ...state,
        interval: action.interval ?? state.interval,
        maxDate: action.maxDate ?? state.maxDate,
        minDate: action.minDate ?? state.minDate,
        range: action.range ?? state.range,
        sources: action.sources,
        clinicalStatus: action.clinicalStatus,
      };
    case Actions.Initialize:
      return {
        ...state,
        interval: action.interval,
        range: action.range,
        maxDate: action.maxDate,
        minDate: action.minDate,
        showAlerts: action.showAlerts ?? state.showAlerts,
        isInitialized: true,
      };
    case Actions.SetInterval:
      return {
        ...state,
        interval: action.interval,
      };
    case Actions.SetView:
      return {
        ...state,
        view: action.view,
      };
    default:
      return state;
  }
}

const Context = React.createContext<IContext>({
  state: initialState,
  dispatch: () => null,
  loading: false,
  data: [],
  // Overwritten by Provider
  category: TrackerType.VitalSigns,
  code: 'test',
  referenceRangeMap: new Map(),
  referenceRange: undefined,
});
Context.displayName = 'Context';

const Provider: React.FC<{
  children: React.ReactNode;
  category: TrackerType;
  code: string;
  showAlerts?: boolean;
}> = ({ children = null, category, code, showAlerts }) => {
  const {
    state: { activeProfile },
  } = useSession();
  const patient = activeProfile?.patient_id ?? '';
  const config = getObservationConfig(code);
  const { graphInterval, graphRange } = config?.graph ?? {};

  const [state, dispatch] = React.useReducer(reducer, initialState);

  const {
    query: getMetricDateRange,
    response: { loading: baseDateFiltersLoading },
  } = useLazyGetMetricDateRange(category, code);

  const isoMaxDate = state.maxDate.toISOString();
  const isoMinDate = state.minDate.toISOString();

  const [data, dataLoading, handleRefetch, fhirReferenceRange] =
    useObservations(code, category, {
      dateFrom: state.minDate.toISOString(),
      dateTo: state.minDate.toISOString(),
    });

  // healthScoreRiskMap (do the same for sleep score)
  const healthScoreRiskMap: Record<string, ReferenceRangeModel['risks']> = {
    'respiratory-score': [
      {
        reference_range_risk_id: '',
        risk: ReferenceRangeRiskLevel.normal,
        parameters: [
          {
            parameter_id: '',
            code: '',
            vitall_patient_id: null,
            lower_bound: {
              inclusive: true,
              value: 90,
            },
          },
        ],
      },
      {
        reference_range_risk_id: '',
        risk: ReferenceRangeRiskLevel.mild,
        parameters: [
          {
            parameter_id: '',
            code: '',
            vitall_patient_id: null,
            lower_bound: {
              inclusive: true,
              value: 89,
            },
            upper_bound: {
              inclusive: true,
              value: 70,
            },
          },
        ],
      },
      {
        reference_range_risk_id: '',
        risk: ReferenceRangeRiskLevel.high,
        parameters: [
          {
            parameter_id: '',
            code: '',
            vitall_patient_id: null,
            upper_bound: {
              inclusive: false,
              value: 70,
            },
          },
        ],
      },
    ],
    'sleep-quality-score': [
      {
        reference_range_risk_id: '',
        risk: ReferenceRangeRiskLevel.normal,
        parameters: [
          {
            parameter_id: '',
            code: '',
            vitall_patient_id: null,
            lower_bound: {
              inclusive: true,
              value: 90,
            },
          },
        ],
      },
      {
        reference_range_risk_id: '',
        risk: ReferenceRangeRiskLevel.mild,
        parameters: [
          {
            parameter_id: '',
            code: '',
            vitall_patient_id: null,
            lower_bound: {
              inclusive: true,
              value: 89,
            },
            upper_bound: {
              inclusive: true,
              value: 70,
            },
          },
        ],
      },
      {
        reference_range_risk_id: '',
        risk: ReferenceRangeRiskLevel.high,
        parameters: [
          {
            parameter_id: '',
            code: '',
            vitall_patient_id: null,
            upper_bound: {
              inclusive: false,
              value: 70,
            },
          },
        ],
      },
    ],
  };
  const referenceRange =
    fhirReferenceRange ??
    (code in healthScoreRiskMap
      ? {
          category,
          code,
          reference_range_id: '',
          risks: healthScoreRiskMap[code],
        }
      : undefined);

  const referenceRangeMap: Map<
    ReferenceRangeRiskLevel,
    ReferenceRangeModel['risks'][number]
  > = React.useMemo(
    () => (referenceRange ? getReferenceRangeMap(referenceRange) : new Map()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(referenceRange)]
  );

  const referenceRangeKey = JSON.stringify(referenceRange);

  const loading = baseDateFiltersLoading || dataLoading;

  React.useEffect(() => {
    async function fetch() {
      if (patient) {
        const initialDateFilters = await getMetricDateRange(patient);
        const { maxDate, minDate } = initialDateFilters;

        const interval = graphInterval
          ? graphInterval
          : initialDateFilters.interval;
        const range =
          (graphRange ?? interval === Interval.Custom)
            ? 1
            : dayjs(maxDate).diff(minDate, DAYJS_MAP[interval]);
        const dateFrom =
          graphRange && graphInterval && graphInterval !== Interval.Custom
            ? dayjs(initialDateFilters.maxDate)
                .subtract(graphRange, DAYJS_MAP[graphInterval])
                .toDate()
            : minDate;

        dispatch({
          type: Actions.Initialize,
          ...initialDateFilters,
          interval,
          range,
          minDate: dateFrom,
          showAlerts,
        });
      }
    }
    if (!state.isInitialized) {
      fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isInitialized, patient]);

  React.useEffect(() => {
    async function fetch() {
      if (patient) {
        await handleRefetch({
          input: {
            patient,
            code,
            dateFrom: isoMinDate,
            dateTo: isoMaxDate,
            count: 3000,
          },
        });
      }
    }
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isoMaxDate, isoMinDate, patient, code]);

  const value: IContext = React.useMemo(
    () => ({
      state,
      dispatch,
      loading,
      data,
      category,
      code,
      referenceRangeMap,
      referenceRange,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, loading, data, category, code, referenceRangeKey]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export {
  Actions as HealthChartActions,
  Context as HealthChartContext,
  Provider as HealthChartProvider,
};
export type { State as HealthChartState, IContext as IHealthChartContext };
